import { useState } from "react";
import {
  Box,
  InputAdornment,
  TableCell,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import TableWrapper from "components/TableWrapper";
import ButtonV1 from "components/ButtonV1";
import Loader from "components/Loader";
import StatusChip from "components/StatusChip";
import useToaster from "hooks/useToaster";
import { fetchESMMMapping } from "services/availabilityService";
import { validateNull } from "utils";
import { ES_MM_MAPPING_COLUMNS } from "constants/availabilityConfig";

const ESMMMapping = () => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const [mmid, setMmid] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const MMID_REGEX = /^\d{4}-\d{5}-\d{8}$/;

  const handleRunMatch = async () => {
    if (!mmid.trim()) {
      triggerToaster("Please enter an FM MMid", "error");
      return;
    }
    if (!MMID_REGEX.test(mmid.trim())) {
      triggerToaster(
        "Invalid MMid format. Please enter a valid MMid (e.g. 1000-10000-10013298)",
        "error",
      );
      return;
    }
    setLoading(true);
    setSearchTerm("");
    const result = await fetchESMMMapping(mmid.trim());
    if (result.error) {
      triggerToaster(result.error || "Failed to fetch mapping", "error");
      setRows([]);
    } else {
      setRows(result.rows);
      if (result.rows.length === 0) {
        triggerToaster("No mapping found for this MMid", "info");
      }
    }
    setLoading(false);
  };

  const renderCell = (field, value) => {
    if (["fm_mmid", "rm_mmid"].includes(field)) {
      return (
        <Typography
          sx={{
            color: theme.palette.accent.main,
            fontWeight: 700,
            fontSize: "0.875rem",
          }}
        >
          {validateNull(value)}
        </Typography>
      );
    }
    if (field === "type") {
      return (
        <StatusChip
          label={value}
          type="INFO"
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            borderRadius: "12px",
            height: 22,
          }}
        />
      );
    }
    return <>{validateNull(value)}</>;
  };

  const filteredRows = searchTerm
    ? rows.filter((row) =>
        ES_MM_MAPPING_COLUMNS.some((col) =>
          String(row[col.field] ?? "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        ),
      )
    : rows;

  const tableHeader = ES_MM_MAPPING_COLUMNS.map((col) => (
    <TableCell
      key={col.field}
      sx={{
        fontWeight: 700,
        whiteSpace: "nowrap",
        backgroundColor: theme.palette.bgColor,
        top: 113,
        zIndex: 9,
      }}
    >
      {col.headerName}
    </TableCell>
  ));

  const tableBody = loading
    ? [
        <TableRow key="loading">
          <TableCell
            colSpan={ES_MM_MAPPING_COLUMNS.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Loader size={28} sx={{ marginTop: 0 }} />
          </TableCell>
        </TableRow>,
      ]
    : filteredRows.length === 0
    ? [
        <TableRow key="empty">
          <TableCell
            colSpan={ES_MM_MAPPING_COLUMNS.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Typography color="textSecondary">
              {searchTerm
                ? "No matching mappings found"
                : mmid
                ? "No mappings found"
                : "Enter a MMid and click Run ES Match"}
            </Typography>
          </TableCell>
        </TableRow>,
      ]
    : filteredRows.map((row, idx) => (
        <TableRow key={row._id ?? idx} hover>
          {ES_MM_MAPPING_COLUMNS.map((col) => (
            <TableCell key={col.field} sx={{ fontSize: "0.85rem", py: 1 }}>
              {renderCell(col.field, row[col.field])}
            </TableCell>
          ))}
        </TableRow>
      ));

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: theme.palette.accent.main }}
        >
          Derived Inventory
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter a FG MMid to find matching RM MMids via Elastic Search
        </Typography>
      </Box>

      {/* Filter row */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Enter FM MMid (e.g. 1000-10000-10013298)"
            size="small"
            value={mmid}
            onChange={(e) => setMmid(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleRunMatch()}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              endAdornment: mmid ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear input"
                    onClick={() => setMmid("")}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <ButtonV1
            title="Run ES Match"
            onClick={handleRunMatch}
            disabled={loading}
            loader={loading}
            style={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              height: 40,
            }}
          />
        </Box>
      </Box>

      {/* Results table */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: theme.palette.accent.main }}
        >
          Saved Mappings {rows.length > 0 && `(${rows.length})`}
        </Typography>
        <TextField
          size="small"
          placeholder="Search mappings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
      </Box>

      <TableWrapper tableHeader={tableHeader} tableBody={tableBody} isSticky />
    </Box>
  );
};

export default ESMMMapping;
